// The Phase 1 scenario demonstration (docs/design/phase1-ui-handoff.md): how newly available,
// authorised synthetic information changes the displayed state, with evidence, record times,
// freshness and clinician-review boundaries. Every panel reads the active stage object. Risk and
// Priority Score are the pack's fixed demonstration outputs, never computed
// (docs/design/phase1-ui-decisions.md).

import { useEffect, useReducer, useRef, useState } from 'react';
import { Banner, Card } from '../components/Layout.tsx';
import { directionText, TrajectoryChart } from '../components/TrajectoryChart.tsx';
import {
  AlertIcon,
  ClockIcon,
  DatabaseIcon,
  EyeIcon,
  HeartIcon,
  InfoIcon,
  NextIcon,
  PauseIcon,
  PlayIcon,
  ResetIcon,
  ShieldIcon,
} from '../icons.tsx';
import { INITIAL, reduce, stageOf, STATUS_COPY } from '../scenario/machine.ts';
import {
  changes,
  contributors,
  COPY,
  PATIENT,
  STAGES,
  TOTAL_STAGES,
  type Change,
  type Stage,
} from '../scenario/stages.ts';

const INTERVALS = [3, 5, 10] as const;

export function ScenarioPage() {
  const [state, dispatch] = useReducer(reduce, INITIAL);
  const [interval, setIntervalSeconds] = useState<(typeof INTERVALS)[number]>(5);
  const stage = stageOf(state);
  const last = state.index >= STAGES.length - 1;

  // Demonstration speed only: each tick moves to the next predefined stage.
  useEffect(() => {
    if (state.status !== 'running') return;
    const timer = setInterval(() => dispatch({ type: 'tick' }), interval * 1000);
    return () => clearInterval(timer);
  }, [state.status, interval]);

  const changed = changes(stage);
  const acknowledged = state.audit.at(-1);

  return (
    <div className="page scenario">
      <div className="page-head">
        <div>
          <div className="title-row">
            <span className="phase">PHASE 1</span>
            <h1>CLINICAL INTELLIGENCE LAYER</h1>
          </div>
          <p className="patient-line">{PATIENT.context}</p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => dispatch({ type: 'clinician-view', open: true })}
        >
          <EyeIcon /> Clinician View
        </button>
      </div>

      <Banner>{COPY.boundary}</Banner>

      <section className="card replay" aria-label="Clinical simulation engine">
        <header className="replay-head">
          <div>
            <h2>1. CLINICAL SIMULATION ENGINE</h2>
            <p className="muted" data-testid="stage-indicator">
              Stage {stage.number} of {TOTAL_STAGES} · {stage.label}
            </p>
          </div>
          <span
            className={`pill ${state.status === 'ready' || state.status === 'completed' ? 'pill-ok' : 'pill-info'}`}
            data-testid="run-status"
          >
            <span className="dot" /> {STATUS_COPY[state.status]}
          </span>
          <div className="replay-controls">
            <button
              type="button"
              className="btn btn-primary"
              disabled={state.status === 'completed'}
              onClick={() => dispatch({ type: state.status === 'running' ? 'pause' : 'start' })}
            >
              {state.status === 'running' ? <PauseIcon /> : <PlayIcon />}
              {state.status === 'running' ? 'Pause' : 'Start Simulation'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={last}
              onClick={() => dispatch({ type: 'next' })}
            >
              <NextIcon /> Next Clinical Event
            </button>
            <button type="button" className="btn" onClick={() => dispatch({ type: 'reset' })}>
              <ResetIcon /> Reset
            </button>
            <label className="interval">
              Simulation interval
              <select
                value={interval}
                onChange={(e) =>
                  setIntervalSeconds(Number(e.target.value) as (typeof INTERVALS)[number])
                }
              >
                {INTERVALS.map((s) => (
                  <option key={s} value={s}>
                    {s} seconds
                  </option>
                ))}
              </select>
            </label>
          </div>
        </header>
        <p className="interval-note">
          <ClockIcon size={14} /> {COPY.intervalExplanation} Clinical record time:{' '}
          <strong>{stage.recordTime} UTC</strong>
        </p>
        <div className="replay-panels">
          <div className="panel">
            <h4>Presenting complaint / reason for attendance</h4>
            <p>{PATIENT.complaint}</p>
            <p className="source">History source: {PATIENT.historySource}</p>
          </div>
          <div className="panel">
            <h4>
              <HeartIcon size={16} /> Initial recorded condition
            </h4>
            <p className="obs-line">{COPY.initialCondition}</p>
            <p className="source">{COPY.initialSource}</p>
          </div>
        </div>
      </section>

      {stage.alert !== undefined && state.alert !== 'none' && (
        <section
          className={`card scenario-alert${state.alert === 'acknowledged' ? ' acknowledged' : ''}`}
          role="alert"
          aria-label={stage.alert.title}
        >
          <AlertIcon size={22} />
          <div className="scenario-alert-body">
            <h3>{stage.alert.title}</h3>
            <p>{stage.alert.detail}</p>
            <p className="muted">{stage.alert.action}</p>
          </div>
          {state.alert === 'active' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => dispatch({ type: 'acknowledge', at: new Date().toISOString() })}
            >
              Acknowledge review
            </button>
          ) : (
            <span className="pill pill-done">
              Review acknowledged
              {acknowledged !== undefined &&
                ` · ${new Date(acknowledged.at).toLocaleTimeString('en-GB')} (demonstration time)`}
            </span>
          )}
        </section>
      )}

      <div className="grid-2">
        <OutputCard
          title={COPY.riskTitle}
          value={`${stage.risk}%`}
          share={stage.risk / 100}
          testId="risk"
        />
        <OutputCard
          title={COPY.scoreTitle}
          value={`${stage.priorityScore} / 100`}
          share={stage.priorityScore / 100}
          testId="priority-score"
        />
      </div>

      <div className="grid-2">
        <Card
          title="Clinical situation summary"
          icon={<InfoIcon />}
          info="The active stage in one place: its record time, what is recorded, what changed and why the display changed."
        >
          <dl className="summary-list">
            <dt>Record time</dt>
            <dd>{stage.recordTime} UTC</dd>
            <dt>Current recorded state</dt>
            <dd>{stage.label}</dd>
            <dt>What changed</dt>
            <dd>{changeSummary(stage)}</dd>
            <dt>Why the display changed</dt>
            <dd>
              {stage.newInformation} ({stage.evidenceSource})
            </dd>
          </dl>
        </Card>
        <Card
          title="Dynamic clinical state"
          icon={<HeartIcon />}
          info="Recorded values of this stage and how they changed. Observation only."
        >
          <p data-testid="physiology">{stage.physiology}</p>
          <p className="note">{COPY.observationOnly}</p>
        </Card>
      </div>

      <div className="grid-3">
        <Card
          title="What changed"
          icon={<InfoIcon />}
          info="Differences from the previous stage, as recorded in the synthetic scenario."
        >
          <p className="stage-label">
            <strong>{stage.recordTime} UTC</strong> · {stage.newInformation}
          </p>
          {stage.changeText !== undefined ? (
            <p>{stage.changeText}</p>
          ) : changed.length === 0 ? (
            <p className="muted">No recorded value changed; new entries are listed as evidence.</p>
          ) : (
            <ul className="changes">
              {changed.map((c) => (
                <li key={c.label}>
                  <span className="change-label">{c.label}</span>
                  <span>
                    {c.from === undefined
                      ? 'first recorded'
                      : `${c.from}${c.unit === '%' ? '%' : ''}`}{' '}
                    → <strong>{withUnit(c.to, c.unit)}</strong>
                  </span>
                  <span className="muted">
                    {c.at} UTC · {c.source}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {stage.number > 1 && <p className="source">Source: {stage.evidenceSource}</p>}
        </Card>

        <Card
          title="Data availability & freshness"
          icon={<DatabaseIcon />}
          info="What the synthetic record contains at this stage, and what it does not yet contain."
        >
          <p className="availability-level">{stage.availability.level}</p>
          <p>Latest record: {stage.recordTime} UTC</p>
          <p className="available">Available: {stage.availability.available}</p>
          <p className="unavailable">{stage.availability.unavailable}</p>
        </Card>

        <Card
          title="Clinical review"
          icon={<ShieldIcon />}
          info="The review boundary of this demonstration. It never makes a clinical decision."
        >
          <p className={`review-status${stage.alert !== undefined ? ' review-flag' : ''}`}>
            {stage.reviewStatus}
          </p>
          <p className="muted">{COPY.imagingBoundary}</p>
          <p className="note">{COPY.phaseBoundary}</p>
        </Card>
      </div>

      <div className="grid-2">
        <Card
          title="Current recorded observations"
          icon={<HeartIcon />}
          info="The latest values in the synthetic record at this stage, with their record time and source."
        >
          <div className="obs">
            {stage.vitals.map((v) => (
              <button
                key={v.key}
                type="button"
                className={`obs-item${state.selected === v.label ? ' obs-selected' : ''}`}
                aria-pressed={state.selected === v.label}
                onClick={() =>
                  dispatch({
                    type: 'select',
                    item: state.selected === v.label ? undefined : v.label,
                  })
                }
                data-vital={v.key}
              >
                <span className="obs-label">{v.label}</span>
                <span className="obs-value">{withUnit(v.value, v.unit)}</span>
                <span className="obs-source">
                  {v.at} UTC · {v.source}
                </span>
              </button>
            ))}
          </div>
        </Card>

        <Card title="Supporting clinical evidence" icon={<DatabaseIcon />}>
          <p className="muted small">{COPY.clinicalTimeLabel}</p>
          <ul className="sources">
            <li>
              <span>Current vital signs</span>
              <span className="muted">
                {stage.recordTime} UTC · {stage.vitals[1]?.source}
              </span>
            </li>
            {stage.vitals[0] !== undefined &&
              stage.vitals[0].source !== stage.vitals[1]?.source && (
                <li>
                  <span>GCS {stage.vitals[0].value}</span>
                  <span className="muted">
                    {stage.vitals[0].at} UTC · {stage.vitals[0].source}
                  </span>
                </li>
              )}
            {stage.evidence.map((e) => (
              <li key={e.label}>
                <span>{e.label}</span>
                <span className="muted">
                  {e.at} UTC · {e.source}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid-2">
        <Card
          title="Risk trajectory"
          icon={<ClockIcon />}
          info="The fixed demonstration output of each stage reached so far. Not a prediction."
        >
          <p className="muted small">{COPY.outputLabel}</p>
          <TrajectoryChart stages={STAGES} index={state.index} total={TOTAL_STAGES} />
          <p className="traj-direction" data-testid="trajectory-direction">
            {directionText(STAGES, state.index)}
          </p>
          <p className="note">
            Stage labels show the predefined simulation order, not elapsed clinical time.{' '}
            {COPY.outputBoundary}
          </p>
        </Card>
        <Card title="Major contributors" icon={<HeartIcon />} info={COPY.contributorNote}>
          <ul className="sources">
            {contributors(stage).map((c) => (
              <li key={c.vital.key} data-contributor={c.vital.key}>
                <span>
                  {c.vital.label} {withUnit(c.vital.value, c.vital.unit)}
                </span>
                <span className="pill pill-muted">{c.tag}</span>
              </li>
            ))}
          </ul>
          <p className="note">{COPY.contributorNote}</p>
        </Card>
      </div>

      <Card title="Prognostic information" icon={<DatabaseIcon />} info={COPY.prognosticBoundary}>
        <ul className="sources">
          {PROGNOSTIC.map((label) => (
            <li key={label}>
              <span>{label}</span>
              <span className="muted">{COPY.prognosticNotConfigured}</span>
            </li>
          ))}
        </ul>
        <p className="note">{COPY.prognosticBoundary}</p>
      </Card>

      <div className="grid-2">
        <Card
          title="Clinical clock"
          icon={<ClockIcon />}
          info="Synthetic clinical record times of each stage. The simulation interval does not change them."
        >
          <ol className="clock">
            {STAGES.map((s, i) => {
              const done = i < state.index;
              const current = i === state.index;
              return (
                <li
                  key={s.number}
                  className={`clock-item${i > state.index ? ' pending' : ''}${current ? ' current' : ''}${s.alert !== undefined && i <= state.index ? ' alert-warning' : ''}`}
                >
                  <span className="clock-dot" />
                  <span className="clock-time">{s.recordTime}</span>
                  <span className="clock-title">
                    Stage {s.number} · {s.label}
                  </span>
                  <span className={`pill ${done || current ? 'pill-done' : 'pill-muted'}`}>
                    {current ? 'Current' : done ? 'Completed' : 'Pending'}
                  </span>
                </li>
              );
            })}
            <li className="clock-item pending gated">
              <span className="clock-dot" />
              <span className="clock-time">16:00</span>
              <span className="clock-title">Stage 5 · {COPY.stage5Pending}</span>
              <span className="pill pill-muted">Awaiting approval</span>
            </li>
          </ol>
        </Card>
        <Card
          title="Simulation audit"
          icon={<ShieldIcon />}
          info="Transient record of this simulation run. Reset clears it. It is not the clinical audit log."
        >
          {state.audit.length === 0 ? (
            <p className="muted">No review actions in this run.</p>
          ) : (
            <ul className="sources">
              {state.audit.map((a) => (
                <li key={a.at}>
                  <span>Review acknowledged · Stage {a.stage}</span>
                  <span className="muted">
                    {new Date(a.at).toLocaleTimeString('en-GB')} (demonstration time)
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {state.alertDialog === true && stage.alert !== undefined && (
        <AlertDialog
          alert={stage.alert}
          onAcknowledge={() => dispatch({ type: 'acknowledge', at: new Date().toISOString() })}
          onClose={() => dispatch({ type: 'close-alert-dialog' })}
        />
      )}

      {state.clinicianView === true && (
        <ClinicianView
          stage={stage}
          onClose={() => dispatch({ type: 'clinician-view', open: false })}
        />
      )}
    </div>
  );
}

/** The pack gives no prognostic values, so every horizon stays "not configured". */
const PROGNOSTIC = [
  'Simulated ICU-admission probability · 6-hour horizon',
  'Simulated ICU-admission probability · 24-hour horizon',
  'Simulated in-hospital mortality probability · 6-hour horizon',
  'Simulated in-hospital mortality probability · 24-hour horizon',
];

/** A fixed demonstration output with the pack's boundary copy. Neutral colour: no severity band. */
function OutputCard({
  title,
  value,
  share,
  testId,
}: {
  title: string;
  value: string;
  share: number;
  testId: string;
}) {
  return (
    <Card title={title} icon={<InfoIcon />} info={COPY.outputBoundary}>
      <p className="output-label">{COPY.outputLabel}</p>
      <p className="output-value" data-testid={testId}>
        {value}
      </p>
      <span className="bar" aria-hidden="true">
        <span className="bar-fill" style={{ width: `${share * 100}%` }} />
      </span>
      <p className="note">{COPY.outputBoundary}</p>
    </Card>
  );
}

function withUnit(value: string, unit: string): string {
  return unit === '%' ? `${value}%` : unit === '' ? value : `${value} ${unit}`;
}

const changeText = (c: Change) =>
  `${c.label} ${c.from === undefined ? 'first recorded' : `${c.from}${c.unit === '%' ? '%' : ''} →`} ${withUnit(c.to, c.unit)}`;

/** "What changed" in one line, from the stage data only. */
function changeSummary(stage: Stage): string {
  if (stage.number === 1) return 'Baseline: no earlier stage to compare with.';
  const changed = changes(stage);
  if (changed.length === 0) return 'No recorded value changed; new entries are listed as evidence.';
  return `${changed.map(changeText).join('; ')}.`;
}

/**
 * The active stage at a glance for a clinician, from the same stage object as the page. No risk,
 * trajectory, score or prognosis (docs/design/phase1-ui-decisions.md). Escape or the button
 * closes it; Reset closes it too.
 */
function ClinicianView({ stage, onClose }: { stage: Stage; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="cv-backdrop" onClick={onClose}>
      <section
        className="cv"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-head">
          <div>
            <h2 id="cv-title">CLINICIAN VIEW</h2>
            <p className="muted">
              {PATIENT.name} · Stage {stage.number} of {TOTAL_STAGES} · {stage.recordTime} UTC
            </p>
          </div>
          <span className="badge badge-demo">{COPY.clinicianView}</span>
        </header>
        <div className="cv-section">
          <h3>Current dynamic clinical state</h3>
          <p>{stage.physiology}</p>
        </div>
        <div className="cv-section">
          <h3>{COPY.outputLabel}</h3>
          <p>
            {COPY.riskTitle}: <strong>{stage.risk}%</strong> · {COPY.scoreTitle}:{' '}
            <strong>{stage.priorityScore} / 100</strong>
          </p>
          <p className="muted">{COPY.outputBoundary}</p>
        </div>
        <div className="cv-section">
          <h3>Current recorded observations</h3>
          <p className="obs-line">
            {stage.vitals.map((v) => `${v.label} ${withUnit(v.value, v.unit)}`).join(' · ')}
          </p>
          <p className="source">
            {stage.recordTime} UTC · {stage.vitals[1]?.source}
          </p>
        </div>
        <div className="cv-section">
          <h3>Most recent clinical changes</h3>
          <p>{changeSummary(stage)}</p>
        </div>
        <div className="cv-section">
          <h3>Data availability</h3>
          <p>
            <strong>{stage.availability.level}</strong> · Available: {stage.availability.available}
          </p>
          <p className="muted">{stage.availability.unavailable}</p>
        </div>
        <div className="cv-section">
          <h3>Clinical review status</h3>
          <p className={stage.alert !== undefined ? 'review-flag' : ''}>{stage.reviewStatus}</p>
          <p className="note">{COPY.phaseBoundary}</p>
        </div>
        <footer className="cv-foot">
          <button ref={close} type="button" className="btn btn-primary" onClick={onClose}>
            Return to Phase 1
          </button>
        </footer>
      </section>
    </div>
  );
}

/**
 * The Stage 4 alert as a dialog, with the pack's approved copy. A view of the same alert state as
 * the alert card (implementation rule 3): Acknowledge review is the same action; Close and Escape
 * only hide the dialog, and the alert stays active.
 */
function AlertDialog({
  alert,
  onAcknowledge,
  onClose,
}: {
  alert: NonNullable<Stage['alert']>;
  onAcknowledge: () => void;
  onClose: () => void;
}) {
  const acknowledge = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    acknowledge.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="cv-backdrop" onClick={onClose}>
      <section
        className="cv alert-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cv-head">
          <h2 id="alert-dialog-title">
            <AlertIcon size={20} /> {alert.title}
          </h2>
        </header>
        <div className="cv-section">
          <p>{alert.detail}</p>
          <div className="alert-change" data-testid="alert-change">
            <span className="output-label">Recorded change</span>
            <strong>
              GCS {alert.prior.value} → {alert.next.value}
            </strong>
            <span>
              {alert.prior.at} UTC → {alert.next.at} UTC · {alert.next.source}
            </span>
          </div>
          <p className="muted">{alert.action}</p>
        </div>
        <footer className="cv-foot">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
          <button
            ref={acknowledge}
            type="button"
            className="btn btn-primary"
            onClick={onAcknowledge}
          >
            Acknowledge review
          </button>
        </footer>
      </section>
    </div>
  );
}
